/**
 * QMS Site Visits Section
 * Collapsible table for managing site visits on client tickets
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Plus, Pencil, Trash2, X, Check } from 'lucide-react';
import { toast } from 'sonner';
import type { QmsSiteVisit } from '../../../types/qms.types';
import { useAddSiteVisit, useUpdateSiteVisit, useDeleteSiteVisit } from '../../../hooks/useQmsSubResources';

interface QmsSiteVisitsSectionProps {
  ticketId: string;
  siteVisits: QmsSiteVisit[];
}

const emptyVisit: Partial<QmsSiteVisit> = {
  date: '',
  address: '',
  comment: '',
};

export function QmsSiteVisitsSection({ ticketId, siteVisits }: QmsSiteVisitsSectionProps) {
  const { t } = useTranslation('qms');
  const [expanded, setExpanded] = useState(siteVisits.length > 0);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editData, setEditData] = useState<Partial<QmsSiteVisit>>({});
  const [isAdding, setIsAdding] = useState(false);
  const [newItem, setNewItem] = useState<Partial<QmsSiteVisit>>(emptyVisit);

  const addMutation = useAddSiteVisit(ticketId);
  const updateMutation = useUpdateSiteVisit(ticketId);
  const deleteMutation = useDeleteSiteVisit(ticketId);

  const handleAdd = async () => {
    try {
      await addMutation.mutateAsync(newItem);
      setIsAdding(false);
      setNewItem(emptyVisit);
      toast.success(t('subResources.siteVisits.added', 'Site visit added'));
    } catch {
      toast.error(t('subResources.siteVisits.addError', 'Failed to add site visit'));
    }
  };

  const handleUpdate = async () => {
    if (!editingId) return;
    try {
      await updateMutation.mutateAsync({ visitId: editingId, data: editData });
      setEditingId(null);
      setEditData({});
      toast.success(t('subResources.siteVisits.updated', 'Site visit updated'));
    } catch {
      toast.error(t('subResources.siteVisits.updateError', 'Failed to update site visit'));
    }
  };

  const handleDelete = async (visitId: string) => {
    if (!confirm(t('subResources.confirmDelete', 'Delete this item?'))) return;
    try {
      await deleteMutation.mutateAsync(visitId);
      toast.success(t('subResources.siteVisits.deleted', 'Site visit deleted'));
    } catch {
      toast.error(t('subResources.siteVisits.deleteError', 'Failed to delete site visit'));
    }
  };

  const startEditing = (visit: QmsSiteVisit) => {
    setEditingId(visit.id);
    setEditData({ date: visit.date, address: visit.address, comment: visit.comment });
  };

  return (
    <div className="border-t pt-4">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-base font-semibold text-gray-900 hover:text-atek-blue"
      >
        {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        {t('form.sections.siteVisits', 'Site Visits')}
        <span className="text-sm font-normal text-gray-500">({siteVisits.length})</span>
      </button>

      {expanded && (
        <div className="mt-3">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-3 py-2 font-medium text-gray-600">{t('form.fields.date', 'Date')}</th>
                  <th className="px-3 py-2 font-medium text-gray-600">{t('form.fields.address', 'Address')}</th>
                  <th className="px-3 py-2 font-medium text-gray-600">{t('form.fields.comment', 'Comment')}</th>
                  <th className="px-3 py-2 font-medium text-gray-600 w-20"></th>
                </tr>
              </thead>
              <tbody>
                {siteVisits.map((visit) => (
                  editingId === visit.id ? (
                    <tr key={visit.id} className="border-b border-gray-100 bg-blue-50">
                      <td className="px-2 py-1"><input type="date" value={editData.date || ''} onChange={(e) => setEditData({ ...editData, date: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" /></td>
                      <td className="px-2 py-1"><input type="text" value={editData.address || ''} onChange={(e) => setEditData({ ...editData, address: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" /></td>
                      <td className="px-2 py-1"><input type="text" value={editData.comment || ''} onChange={(e) => setEditData({ ...editData, comment: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" /></td>
                      <td className="px-2 py-1 flex gap-1">
                        <button type="button" onClick={handleUpdate} disabled={updateMutation.isPending} className="p-1 text-green-600 hover:bg-green-100 rounded"><Check className="h-4 w-4" /></button>
                        <button type="button" onClick={() => setEditingId(null)} className="p-1 text-gray-500 hover:bg-gray-100 rounded"><X className="h-4 w-4" /></button>
                      </td>
                    </tr>
                  ) : (
                    <tr key={visit.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-900">{visit.date || '-'}</td>
                      <td className="px-3 py-2 text-gray-900">{visit.address || '-'}</td>
                      <td className="px-3 py-2 text-gray-900">{visit.comment || '-'}</td>
                      <td className="px-3 py-2 flex gap-1">
                        <button type="button" onClick={() => startEditing(visit)} className="p-1 text-gray-500 hover:bg-gray-100 rounded"><Pencil className="h-3.5 w-3.5" /></button>
                        <button type="button" onClick={() => handleDelete(visit.id)} disabled={deleteMutation.isPending} className="p-1 text-red-500 hover:bg-red-100 rounded"><Trash2 className="h-3.5 w-3.5" /></button>
                      </td>
                    </tr>
                  )
                ))}
                {isAdding && (
                  <tr className="border-b border-gray-100 bg-green-50">
                    <td className="px-2 py-1"><input type="date" value={newItem.date || ''} onChange={(e) => setNewItem({ ...newItem, date: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" /></td>
                    <td className="px-2 py-1"><input type="text" value={newItem.address || ''} onChange={(e) => setNewItem({ ...newItem, address: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" placeholder="Address" /></td>
                    <td className="px-2 py-1"><input type="text" value={newItem.comment || ''} onChange={(e) => setNewItem({ ...newItem, comment: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" placeholder="Comment" /></td>
                    <td className="px-2 py-1 flex gap-1">
                      <button type="button" onClick={handleAdd} disabled={addMutation.isPending} className="p-1 text-green-600 hover:bg-green-100 rounded"><Check className="h-4 w-4" /></button>
                      <button type="button" onClick={() => setIsAdding(false)} className="p-1 text-gray-500 hover:bg-gray-100 rounded"><X className="h-4 w-4" /></button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {!isAdding && (
            <button type="button" onClick={() => setIsAdding(true)} className="mt-2 flex items-center gap-1 text-sm text-atek-blue hover:text-atek-blue-dark">
              <Plus className="h-4 w-4" /> {t('subResources.siteVisits.add', 'Add Site Visit')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
